<template>
  <div v-if="choices.length" class="k-choices">
    <div class="d-flex">
      <div
        v-for="(choice, index) in choices"
        :key="choice.id"
        :class="[cssMode, index === 0 ? '' : 'k-border-left']"
        class="k-choice-details"
      >
        <div :class="cssDate(choice)" class="choice-date">
          <div
            v-if="showTrophy(choice)"
            class="max-votes text-size-24"
            data-toggle="tooltip"
            :title="$t('polls.pokalToolTip')"
          >
            <font-awesome-icon
              class="text-secondary mr-1"
              :icon="['fas', 'trophy']"
            />
          </div>
          <div v-else class="empty max-votes"></div>
          <div class="p-1" v-if="isDateType">
            <div class="grid-weekDay text-size-14 text-weight-regular">
              {{ weekDay(choice) }}
            </div>
            <div class="dayMonth mx-3 text-size-28 text-weight-semi-bold">
              {{ dayMonth(choice) }}
            </div>
            <div
              v-if="choice.start != choice.end"
              class="timeRange text-size-14 text-weight-regular"
            >
              <span v-if="choice.start">{{ choice.start | timeRange }}</span>
              <span v-else>-</span>
              <span v-if="choice.end">-{{ choice.end | timeRange }}</span>
            </div>
            <div v-else class="timeRange text-size-14 text-weight-regular">
              <span>-</span>
            </div>
          </div>
          <div
            class="text-size-14 justify-content-center align-bottom text-weight-semi-bold flex-grow-1 px-2"
            :class="cssChoice(choice.id)"
          >
            {{ choice.text | textComment }}
          </div>
          <transition
            v-on:after-enter="afterEnter"
            v-on:after-leave="afterLeave"
            name="fadeHeight2"
          >
            <div
              v-if="submittingParticipant"
              class="submit-row d-flex justify-content-center align-items-start"
              :style="submitRowHeight"
              :class="sibmitColumnCss(choices, index)"
            >
              <div
                class="render-preference justify-content-center align-items-center"
              >
                <div
                  :class="{
                    'form-border pointer': visibleFormBorder(choice, submittingParticipant),
                  }"
                  class="form-border-height d-flex justify-content-center align-items-center"
                >
                  <renderPreference
                    :loading="isLoading(submittingParticipant, choice.id)"
                    :submitMode="true"
                    :showEditButton="true"
                    :isDisabled="isDisabled(submittingParticipant, choice)"
                    :isLimitReached="isLimitReached(choice)"
                    :value="getAnswerForChoice(submittingParticipant, choice)"
                    :isEditable="true"
                    :isDesktopMode="true"
                    @click.native="
                      toggleChoiceIfEnabled(
                        $event,
                        submittingParticipant,
                        choice
                      )
                    "
                    :class="[
                      {
                        'bg-white': showBgWhite(submittingParticipant, choice),
                      },
                      cssMode,
                    ]"
                    :isCurrentUser="false"
                  />
                </div>

                <RenderDescriptionOfAnswer
                  v-if="!isLimitReached(choice)"
                  :participant="submittingParticipant"
                  :choice="choice"
                />

                  <RenderAnswerSelection
                    :participant="submittingParticipant"
                    :choice="choice"
                  />
                <div v-if="0" class="choice-data justify-content-center">
                  <div
                    class="mb-2"
                    v-for="(answer, index) in choiceConfig"
                    :key="answer.id"
                  >
                    <div
                      v-if="
                        isAnswerVisible(
                          getAnswerForChoice(submittingParticipant, choice),
                          index
                        )
                      "
                    >
                      <template
                        v-if="!isParticipantAllowedToSetTheAnswer(answer)"
                      >
                        <div
                          style="word-wrap: break-word;"
                          class="text-size-12"
                        >
                          {{ answer.labelValue }}
                        </div>
                        <div
                          style="word-wrap: break-word;"
                          class="text-size-16 pb-2"
                        >
                          {{
                            getPreferenceAnswerValueForDisplay(
                              submittingParticipant,
                              choice,
                              index
                            )
                          }}
                        </div>
                      </template>
                      <template v-else>
                        <InputSelectField
                          :label="answer.labelValue"
                          :options="answer.values.map((option) => option.value)"
                          :value="
                            getPreferenceAnswer(
                              submittingParticipant,
                              choice.id,
                              index
                            )
                          "
                          @select="
                            onSelectOptions(
                              $event,
                              submittingParticipant,
                              choice.id,
                              index
                            )
                          "
                        />
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition>
          <div
            v-if="showCounts"
            class="yes-counts text-weight-semi-bold mt-2 mb-1 pointer"
            @click="showInfo(choice.id)"
          >
            <font-awesome-icon
              class="k-checked mr-1"
              size="xs"
              :icon="['fas', 'check']"
            />

            {{ countYesAnswers(choice) }}
            <span v-if="showLimitCount(choice)"
              >/{{ votesChoiceLimit(choice) }}</span
            >
            <font-awesome-icon class="ml-1" :icon="['fas', 'info-circle']" />
          </div>
          <div v-else class="yes-counts text-weight-semi-bold mt-2 mb-1"></div>
        </div>
        <!-- END choice header (dates) -->

        <div class="k-participant-choices">
          <div
            v-for="participant in participantList(submittingParticipant)"
            :key="participant.id"
            class="preference_list"
          >
            <renderPreference
              :value="getAnswerForChoice(participant, choice)"
              :preference="getPreferenceForChoice(participant, choice.id)"
              :isLimitReached="isLimitReached(choice)"
              class="k-choice"
              :class="[cssMode]"
              :isCurrentUser="participant.isCurrentUser"
              :showNameOfQuestion="true"
              :showEditButton="false"
            />
          </div>
          <!-- END v-for participants -->

          <div
            v-for="invitation in openInvitations"
            :key="invitation.id"
            class="k-choice"
            :class="cssMode"
          >
            <font-awesome-icon text-size-14 :icon="['fas', 'question']" />
          </div>
          <!-- END v-for openInvitations -->
        </div>
        <!-- END choice details (answers) -->
      </div>
    </div>
    <ModalParticipantInfoDialog
      ref="dlgPaymentDialog"
      :participants="userData"
      :isOwner="isOwner"
    >
    </ModalParticipantInfoDialog>
  </div>
  <!-- END v-for choices -->
</template>
<script>
//import { HTTP } from '@/shared/http';
//import { mapState, mapGetters, mapActions } from 'vuex';
import { renderMixin } from '../mixins/RenderMixins';
import { gridMixin } from '../mixins/GridMixins';
import { answerMixin } from '../mixins/AnswerMixins.js';
import RenderPreference from './RenderPreference';
import { mapState } from 'vuex';

import RenderDescriptionOfAnswer from './RenderDescriptionOfAnswer.vue';
import RenderAnswerSelection from './RenderAnswerSelection.vue';

export default {
  name: 'RendererChoices',
  props: ['choices', 'dlgChoiceDialog', 'submittingParticipant'],
  mixins: [renderMixin, gridMixin, answerMixin],

  data() {
    return {
      list: [],
      userData: [],
    };
  },
  components: {
    RenderPreference,
    RenderDescriptionOfAnswer,
    RenderAnswerSelection,
  },

  computed: {
    ...mapState({
      participantBackup: (state) => state.poll.participantBackup,
    }),

    maxVotes() {
      let maxVotes = 0;
      for (let choice of this.choices) {
        const colVotes = this.countYesAnswers(choice);
        if (colVotes > maxVotes) {
          maxVotes = colVotes;
        }
      }
      return maxVotes;
    },
    pollWithLimit() {
      if (this.poll.options.limitedVotesPerChoice) {
        return true;
      }
      if (this.poll.options.limitedVotesPerChoiceSpecific) {
        return true;
      }
      return false;
    },
  },

  methods: {
    showTrophy(choice) {
      if (this.pollWithLimit) return false;


      if (this.poll.options.hiddenPoll && !this.poll.options.showCounts) {
        return false;
      }
      const count = this.countYesAnswers(choice);
      if (count == 0) return false;
      if (count == this.maxVotes) return true;
      return false;
    },

    votesChoiceLimit(choice) {
      if (
        !(
          this.poll.options.limitedVotesPerChoice ||
          this.poll.options.limitedVotesPerChoiceSpecific
        )
      )
        return 256;
      if (this.poll.options.limitedVotesPerChoice) {
        return this.poll.options.votesLimit;
      }
      return choice.limit;
    },

    // isAnswerVisible(value, index) {
    //   switch (this.choiceConfig[index].main_answer.code) {
    //     case 'always':
    //       return true;
    //     case 'main_answer_is_yes':
    //       return value > 0;
    //     case 'main_answer_is_no':
    //       return value == 0;
    //   }
    // },

    showInfo(choiceId) {
      if (this.isHiddenParticipantMode) return;
      console.info('send showInfo event:', choiceId);
      this.$emit('showInfo', choiceId);
      // this.userData = this.getAllParticipantOfChoice(choiceId);
      // this.$nextTick(() => {
      //   this.$refs.dlgPaymentDialog.openModal();
      // });
    },

    afterEnter() {
      this.$emit('afterEnter');
    },

    afterLeave() {
      this.$emit('afterLeave');
    },

    showBgWhite(participant, choice) {
      if (this.isBookingMode && !this.isOwner) {
        if (this.participantBackup) {
          let preferenceForChoice = this.participantBackup.preference_list.find(
            (preference) => preference.choice_id === choice.id
          );
          if (preferenceForChoice.value >= 0) return false;
        }
      }

      if (this.isLimitReached(choice)) {
        const value = this.getAnswerForChoice(participant, choice);
        if (value < 1) return false;
      }
      return true;
    },

    // visibleFormBorder(choice) {
    //   if (this.isBookingMode && !this.isOwner) {
    //     if (this.participantBackup) {
    //       let preferenceForChoice = this.participantBackup.preference_list.find(
    //         (preference) => preference.choice_id === choice.id
    //       );
    //       if (preferenceForChoice.value >= 0) return false;
    //     }
    //   }

    //   if (!this.isLimitReached(choice)) return true;
    //   const value = this.getAnswerForChoice(this.submittingParticipant, choice);
    //   if (value > 0) return true;
    //   return false;
    // },

    sibmitColumnCss(choices, index) {
      if (choices.length == index + 1) {
        return 'rounded-right-corners';
      }
      return null;
    },

    toggleChoiceIfEnabled($event, participant, choice) {
      if (this.submittingParticipant != participant) return false;
      if (this.isDisabled(participant, choice)) {
        return;
      }
      this.toggleChoice($event, participant, choice);
    },

    cssChoice(id) {
      if (this.poll.type === 1) return 'choice-text';
      return 'choice-text-survey choice-' + id;
    },
    cssDate(choice) {
      if (this.poll.closed && choice.final) return this.cssMode + ' bg-closed';
      if (this.isLimitReached(choice)) return this.cssMode + ' bg-completed';
      return this.cssMode;
    },

    cssVotes(choice) {
      if (this.isLimitReached(choice)) return ' bg-completed';
      return '';
    },

    isLimitReached(choice) {
      return this.isChoiceVotesLimitReached(0, choice);
    },
  },

  mounted() {
    this.list = this.choices;
  },
};
</script>
<style scoped lang="scss">
@import '@/styles/style.scss';
@import '@/styles/submit-form';

.choice-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0.3em;
  padding: 0;
  padding-left: 0;
  padding-right: 0;
  flex-grow: 1;
}

.choice-date.light-mode {
  border-bottom: 1px solid $grid-border;
}

.choice-date.dark-mode {
  border-bottom: 1px solid white;
}

.yes-counts {
  font-size: 16px;
  height: 38px;
  display: flex;
  align-items: center;
}

.form-border {
  border: 1px solid;
  border-radius: 3px;
  padding: 0px;
  margin: 0px;
  width: 48px;
  height: 48px;
}

.form-border-height {
  height: 48px;
}

.choice-response-description {
  text-align: center;
  font-size: $so-small;
  min-height: 20px;
}

.bg-completed {
  background-color: $complete-color;
}

.bg-closed {
  background-color: $bg-closed;
}

.render-preference {
  margin-top: 52px;
  display: flex;
  flex-direction: column;
}
.info-line {
  position: absolute;
  top: 130px;
  color: $mid_grey;
}

.underline {
  text-decoration: underline;
}

.max-votes {
  height: 40px;
}

.preference_list {
  display: flex;
  flex-direction: column;
}
</style>
